import type { DecisionReport, ShareSnapshot } from "./types";

interface PrivateDecisionState {
  report: DecisionReport;
  rawAnswers?: string[];
  privateNotes?: string;
}

export function buildShareSnapshot(state: PrivateDecisionState): ShareSnapshot {
  return {
    version: 1,
    report: structuredClone(state.report),
    sharedAt: new Date().toISOString(),
  };
}

export function redactPrivateAnswers(report: DecisionReport, answers: string[]): DecisionReport {
  const needles = answers
    .map((answer) => answer.trim())
    .filter((answer) => answer.length >= 8)
    .flatMap((answer) => answer.length > 18 ? [answer, answer.slice(0, 18)] : [answer])
    .sort((a, b) => b.length - a.length);
  const serialized = JSON.stringify(report, (_key, value: unknown) => {
    if (typeof value !== "string") return value;
    return needles.reduce((text, needle) => text.replaceAll(needle, "（私人回答已隱去）"), value);
  });
  return JSON.parse(serialized) as DecisionReport;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

export function isDecisionReport(value: unknown): value is DecisionReport {
  if (!value || typeof value !== "object") return false;
  const report = value as Partial<DecisionReport>;
  return (
    report.version === 1 &&
    typeof report.title === "string" &&
    typeof report.summary === "string" &&
    typeof report.recommendation === "string" &&
    ["low", "medium", "high"].includes(report.confidence ?? "") &&
    Array.isArray(report.options) &&
    Array.isArray(report.criteria) &&
    isStringArray(report.blindSpots) &&
    isStringArray(report.openQuestions) &&
    isStringArray(report.nextSteps) &&
    typeof report.coachNote === "string" &&
    !!report.scenarios &&
    typeof report.scenarios.optimistic === "string" &&
    typeof report.scenarios.likely === "string" &&
    typeof report.scenarios.downside === "string"
  );
}

export function validateShareSnapshot(value: unknown): ShareSnapshot | null {
  if (!value || typeof value !== "object") return null;
  const snapshot = value as Partial<ShareSnapshot>;
  if (
    snapshot.version !== 1 ||
    typeof snapshot.sharedAt !== "string" ||
    !isDecisionReport(snapshot.report)
  ) {
    return null;
  }
  return snapshot as ShareSnapshot;
}
